import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from 'util/dayjs-init.js';
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";

import DownloadIcon from "./downloadIcon.jsx";

import { getSepDataDuration, getElectricDataDuration, getCustomDataDuration } from "../../../model/slice/dataDurationSlice";
import {
  selectedVessel,
  setSelectedVessel,
} from "../../../model/slice/fleetNavigationDataSlice.js";
import {
  createCsvDownload,
  oneSecDownload,
  useCsvDownload,
} from "../../../helpers/csvDownload/createCSV";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType.js";
import { chartsData, chartsSettings } from "../../../model/slice/chartsSlice.js";
import { calDurations } from "../../../constants/calDuration.js";
import { selectedTimezone } from "../../../model/slice/timezoneSlice.js";
import { TIMEZONE } from "../../../constants/timezone.js";
import { formatDate } from "../../../util/timezone.js";
import { DATE_FORMAT, DATE_FORMAT_JST } from "../../../constants/constants.js";
import { formatGraphDataForCSV } from "../../../util/trendPage/csvDownload.js";
import { FULL_DATE_TIME_FORMAT } from "../../../constants/util.js";

const styles = {
  downloadIcon: {
    width: "25px",
    height: "25px",
    padding: "0px 0px",
    minWidth: "0px",
    marginRight: "10px",
  },
};

export const CsvDownload = React.memo(
  ({
    vesselId,
    isSepVessel,
    currentVessel,
    graphData,
    graphType,
    selectedChart,
    selectedEngine,
    isDownloading,
    setIsDownloading,
    isDurationDisabled,
    isNoData,
    is1SecFetchEnabled,
    isComparison = false,
    graphRef,
    vesselName,
    isCargo,
    chartsNameSettings,
    customChartsList,
    graphConfig,
    startDate,
    endDate,
  }) => {
    const dispatch = useDispatch();
    const customChartData = useSelector(chartsData);
    const customChartSettings = useSelector(chartsSettings);
    const sepDuration = useSelector(getSepDataDuration);
    const electricDuration = useSelector(getElectricDataDuration);
    const customDuration = useSelector(getCustomDataDuration);
    const currentSelectedVessel = useSelector(selectedVessel);
    const timezone = useSelector(selectedTimezone);
    const currentVesselName = currentSelectedVessel?.vessel_name;
    const isCustom = graphType === GRAPH_TYPE.CUSTOM;
    useEffect(() => {
      if (!currentSelectedVessel) {
        dispatch(setSelectedVessel(vesselId));
      }
    }, []);


    const formatChartName = (selectedChart, isCustom, isComparison) => {
      if (!isCustom) return selectedChart;
      const returnVal = isComparison 
        ? customChartsList?.charts?.charts?.find((v) => v.id === selectedChart)
        : customChartData?.charts?.find((v) => v.id === selectedChart);
      return returnVal.name.replace(/\s/g, "-");
    };

    const formattedData = useCallback( 
      (
        formatGraphDataForCSV(graphData, isCustom, chartsNameSettings ?? customChartSettings, selectedChart, graphConfig))
          .map((data, idx) => {
            data.downloadDateTime = formatDate(data.dateTime, timezone, timezone === TIMEZONE.UTC ? DATE_FORMAT : DATE_FORMAT_JST);
            delete data.localDateTime;
            return data;
          }),
      [graphData, graphConfig, timezone]);
    const { blob } = useCsvDownload({ data: formattedData, formatter: "," });
    const downloadCsvOnClick = async () => {
      if (isDownloading) return;

      let currentDuration = sepDuration;
      // comparison use each graph date time.
      if(isComparison) {
        const formattedStartDate = timezone === TIMEZONE.UTC ? dayjs.utc(startDate).format(FULL_DATE_TIME_FORMAT) : dayjs(startDate).format(FULL_DATE_TIME_FORMAT);
        const formattedEndDate = timezone === TIMEZONE.UTC ? dayjs.utc(endDate).format(FULL_DATE_TIME_FORMAT) : dayjs(endDate).format(FULL_DATE_TIME_FORMAT);
        currentDuration = {startDate: formattedStartDate, endDate: formattedEndDate};
      } else if (graphType === GRAPH_TYPE.ELECTRIC) {
        currentDuration = electricDuration;
      } else if (graphType === GRAPH_TYPE.CUSTOM) {
        currentDuration = customDuration;
      }

      const formattedName = formatChartName(selectedChart, isCustom, isComparison);
      const formattedDate = `${(timezone === TIMEZONE.UTC ? dayjs.utc() : dayjs()).format("YYYY-MM-DD-HH-mm-ss")}(${timezone})`;
      const do1SecFetch = () => calDurations({ startDate: currentDuration.startDate, endDate: currentDuration.endDate}) <= 1;
      if (
        is1SecFetchEnabled
        && isSepVessel && graphType !== GRAPH_TYPE.CUSTOM
        && do1SecFetch()
      ) {
        await oneSecDownload({
          vesselId,
          startDate: currentDuration?.startDate,
          endDate: currentDuration?.endDate,
          filename: `${
            isComparison
              ? vesselName?.replace(/\s/g, "-")
              : currentVesselName?.replace(/\s/g, "-") ?? vesselId
          }_${graphType}_${formattedName}_${formattedDate}`,
          selectedGraphType: graphType,
          selectedChart,
          setIsDownloading,
          isDownloading,
          timezone,
          ge: currentVessel?.NoDG,
          sg: currentVessel?.NoSG,
          customChartSettings: isCustom ? customChartSettings : [],
        });
        return;
      }
      if (!blob) return;

      createCsvDownload({
        blob,
        filename:
          isComparison
            ? `${vesselName?.replace(/\s/g, "-") ?? vesselId}_${graphType}_${
                selectedEngine ? selectedEngine.split("_")[0] + "_" : ""
              }${formattedName}_${formattedDate}`
            : `${currentVesselName?.replace(/\s/g, "-") ?? vesselId}_${graphType}_${
                selectedEngine ? selectedEngine.split("_")[0] + "_" : ""
              }${formattedName}_${formattedDate}`,
      });
    };
    const shouldBeDisabled = (isDurationDisabled || blob === false || isNoData || isDownloading);
    return (
      <Button ref={graphRef} sx={styles.downloadIcon} disabled={shouldBeDisabled} onClick={() => downloadCsvOnClick()}>
        <DownloadIcon
          color={shouldBeDisabled ? "#ffffff" : "#D8D8D8"}
          opacity={shouldBeDisabled ? "0.5" : "1"}
        />
      </Button>
    );
  }
);

CsvDownload.propTypes = {
  vesselId: PropTypes.string,
  currentVessel: PropTypes.object,
  isSepVessel: PropTypes.bool,
  graphData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  graphType: PropTypes.string,
  selectedChart: PropTypes.string,
  selectedEngine: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isDownloading: PropTypes.bool,
  setIsDownloading: PropTypes.func,
  isDurationDisabled: PropTypes.bool,
  isNoData: PropTypes.bool,
  is1SecFetchEnabled: PropTypes.bool,
  isComparison: PropTypes.bool,
  graphRef: PropTypes.any,
  vesselName: PropTypes.string,
  isCargo: PropTypes.bool,
  chartsNameSettings: PropTypes.array,
  customChartsList: PropTypes.array,
  graphConfig: PropTypes.object,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
